import { cn } from '@/lib/utils'
import React, { useEffect, useState } from 'react'
import { Input } from '../ui'
import type { Period, TimePickerType } from './utils'
import { getArrowByType, getDateByType, setDateByType } from './utils'

export interface TimePickerInputProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'value' | 'onChange'
  > {
  ref?: React.Ref<HTMLInputElement>

  value: Date
  picker: TimePickerType
  period?: Period
  onRightFocus?: () => void
  onLeftFocus?: () => void
  onChange(date: Date): void
}

export function TimePickerInput({
  ref,
  className,

  value,

  type = 'tel',
  id,
  name,
  picker,
  period,

  onChange,
  onKeyDown,
  onLeftFocus,
  onRightFocus,
  ...props
}: TimePickerInputProps) {
  const [flag, setFlag] = useState(false)
  const [prevIntKey, setPrevIntKey] = useState('0')

  /**
   * allow the user to enter the second digit within 2 seconds
   * otherwise start again with entering first digit
   */
  useEffect(() => {
    if (flag) {
      const timer = setTimeout(() => {
        setFlag(false)
      }, 2000)

      return () => clearTimeout(timer)
    }
  }, [flag])

  const calculatedValue = getDateByType(value, picker)

  function calculateNewValue(key: string) {
    /*
     * If picker is '12hours' and the first digit is 0, then the second digit is automatically set to 1.
     * The second entered digit will break the condition and the value will be set to 10-12.
     */
    if (picker === '12hours') {
      if (flag && calculatedValue.slice(1, 2) === '1' && prevIntKey === '0')
        return '0' + key
    }

    return !flag ? '0' + key : calculatedValue.slice(1, 2) + key
  }

  function handleKeyDown(evt: React.KeyboardEvent<HTMLInputElement>) {
    if (evt.key === 'Tab') return
    evt.preventDefault()

    if (evt.key === 'ArrowRight') onRightFocus?.()
    if (evt.key === 'ArrowLeft') onLeftFocus?.()
    if (['ArrowUp', 'ArrowDown'].includes(evt.key)) {
      const step = evt.key === 'ArrowUp' ? 1 : -1
      const newValue = getArrowByType(calculatedValue, step, picker)
      if (flag) setFlag(false)
      const tempDate = new Date(value)
      onChange(setDateByType(tempDate, newValue, picker, period))
    }
    if (evt.key >= '0' && evt.key <= '9') {
      if (picker === '12hours') setPrevIntKey(evt.key)

      const newValue = calculateNewValue(evt.key)
      if (flag) onRightFocus?.()
      setFlag(prev => !prev)

      const tempDate = new Date(value)
      onChange(setDateByType(tempDate, newValue, picker, period))
    }
  }

  return (
    <Input
      ref={ref}
      className={cn(
        'w-12 h-15.5 px-2 text-center font-mono text-base tabular-nums caret-transparent',
        'focus:bg-accent focus:text-accent-foreground',
        '[&::-webkit-inner-spin-button]:appearance-none',
        className
      )}
      id={id || picker}
      value={calculatedValue}
      name={name || picker}
      type={type}
      inputMode="decimal"
      onKeyDown={evt => {
        onKeyDown?.(evt)
        handleKeyDown(evt)
      }}
      onChange={(_, evt) => evt.preventDefault()}
      {...props}
    />
  )
}
